import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCuil } from '../utils/formatCuil';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    Grid,
    Typography,
    Stack,
    Snackbar,
    Alert,
    IconButton
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { crearCliente, consultarExistenciaCuil } from '../services/clienteService';
import { getNivelesEducativos } from '../services/nivelEducativoService';
import { getProvincias } from '../services/provinciaService';
import { getLocalidadesPorProvincia, getLocalidadesPorDivision } from '../services/localidadService';
import { getDivisionesPorProvincia } from '../services/divisionPrimariaService';
import SelectorReferenciaCliente from './SelectorReferenciaCliente';
import DatosMaestrosService from '../services/datosMaestrosService';
import { isTelefonoCordobaValido, normalizeTelefonoForStorage } from '../utils/telefonoCordoba';
import { apellidoNombre } from '../utils/nombrePersona';
import { AvisoYaFueCliente } from './PlanillaHistorica/PlanillaHistorica';

function getFechaHoy() {
    const hoy = new Date();
    return hoy.toISOString().split('T')[0];
}

// Estructura inicial idéntica a la que usa VistaDetalladaCliente
const clienteInicial = {
    cuil: '',
    apellido: '',
    nombre: '',
    fechaIngreso: getFechaHoy(),
    mail: '',
    dosDoce: false,
    telefonoPropio: '',
    referencia: '',
    idReferencia: null,
    cuilClienteReferente: null,
    nombreClienteReferente: '',
    nombreReferenteExterno: null,
    observacion: '',
    oficio: '',
    activo: true,
    nivelEducativo: { id: '' },
    clave: {
        claveAfip: '',
        claveSs: '',
        claveCidi: ''
    },
    genero: '',
    estadoCivil: '',
    conyugeNombre: '',
    tieneObraSocial: false,
    mantieneObraSocialPostJubilacion: false,
    nombreObraSocial: ''
};

const contactoInicial = {
    nroTelefono: '',
    relacion: '',
    nombre: ''
};

// Límites de longitud según las columnas de la base (evita "value too long for type
// character varying" — ver incidente del 09/09/2026, ClienteEntity/ContactoEntity/DomicilioEntity).
const LIMITES = {
    apellido: 50,
    nombre: 50,
    referencia: 50,
    mail: 100,
    oficio: 100,
    observacion: 1000,
    conyugeNombre: 150,
    nombreObraSocial: 150,
    nombreReferenteExterno: 150,
    contactoNombre: 100,
    domicilioBarrio: 50,
    domicilioCalle: 100,
    domicilioDepartamento: 10,
    hijoNombre: 150,
};

const hijoVacio = () => ({
    nombre: '',
    fechaNacimiento: '',
    tieneAuh: false,
    tieneCud: false,
    esAdoptado: false
});

// Deriva la edad a partir de la fecha de nacimiento (YYYY-MM-DD); sin fecha, sin edad.
const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return '';
    const hoy = new Date();
    const nac = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nac.getFullYear();
    const m = hoy.getMonth() - nac.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
    return edad >= 0 ? edad : '';
};



const domicilioVacio = () => ({
    calle: '',
    altura: '',
    piso: '',
    departamento: '',
    barrio: '',
    tipo: '',
    localidad: { id: '', localidad: '', provincia: { id: '' } },
    _provinciaId: '',
    _divisionId: '',
    _divisiones: [],
    _localidades: []
});

const limpiarDomicilioParaGuardar = ({ _provinciaId, _divisionId, _divisiones, _localidades, ...d }) => d;

/**
 * Alta de cliente. Se usa suelta (ruta /clientes/registrar, listados) y también embebida dentro
 * de otra pantalla que quedó a medio completar: la ficha de atención y los 13 formularios de
 * alta de trámite la abren cuando el cliente todavía no existe.
 *
 * `mostrarDialogoConfirmacion` decide si, después de guardar, se ofrece el diálogo
 * "¿Qué desea hacer a continuación?" con la lista de trámites. Ese diálogo NAVEGA
 * (`navigate` a /{modulo}/tramites/iniciar-tramite/:cuil), y esas rutas renderizan el formulario
 * sin ninguna prop — con lo cual se pierde todo el contexto de quien abrió el modal.
 *
 * Por eso: **quien embeba este modal dentro de una pantalla con estado en progreso tiene que
 * pasar `mostrarDialogoConfirmacion={false}`** y quedarse con el cliente que llega por
 * `onSuccess`. Si no, el operador se va a otra página y lo que estaba haciendo se pierde.
 *
 * No es teórico: durante septiembre de 2026 los formularios de alta de trámite no pasaban el
 * flag, y 20 trámites abiertos durante una atención quedaron con `id_turno_origen = NULL`. Eso
 * dejó al embudo de Reportes Recepción marcando 58 conversiones cuando las reales eran 74.
 * Hay un test que lo cubre en ModalRegistrarCliente.turnoOrigen.test.js y otro que verifica que
 * ningún formulario de alta se olvide del flag.
 */
export default function ModalRegistrarCliente({ open, onClose, onSuccess, mostrarDialogoConfirmacion = true, crearTramitesPendientes = false, datosIniciales = null }) {
    const navigate = useNavigate();

    // Estado del cliente con estructura completa
    const [cliente, setCliente] = useState({
        ...clienteInicial,
        fechaNacimiento: '',
    });
    const [contactos, setContactos] = useState([contactoInicial]);
    const [hijos, setHijos] = useState([]);
    const [edad, setEdad] = useState('');
    const [referenciasDisponibles, setReferenciasDisponibles] = useState([]);

    // Estados auxiliares
    const [provincias, setProvincias] = useState([]);
    const [domicilios, setDomicilios] = useState([domicilioVacio()]);
    const [niveles, setNiveles] = useState([]);

    // Estados de UI
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [dialogoConfirmar, setDialogoConfirmar] = useState(false);
    const [dialogoPaso, setDialogoPaso] = useState(1);
    const [clienteCreado, setClienteCreado] = useState(null);
    // Cliente que ya tiene el CUIL escrito ({ activo, apellido, nombre }), o null si no hay ninguno.
    const [cuilExistente, setCuilExistente] = useState(null);

    // Resetear formulario cuando se abre el modal; pre-rellenar si vienen datosIniciales
    useEffect(() => {
        if (open) {
            const cuil = datosIniciales?.cuil
                ? formatCuil(datosIniciales.cuil)
                : '';
            const telefono = datosIniciales?.telefonoCliente
                ? datosIniciales.telefonoCliente.replace(/\D/g, '').slice(-10)
                : '';
            setCliente({
                ...clienteInicial,
                fechaIngreso: getFechaHoy(),
                fechaNacimiento: '',
                cuil,
                apellido: datosIniciales?.apellido || '',
                nombre: datosIniciales?.nombre || '',
                telefonoPropio: telefono,
                referencia: datosIniciales?.referencia || '',
                idReferencia: datosIniciales?.idReferencia ?? null,
            });
            setContactos([{ ...contactoInicial }]);
            setDomicilios([domicilioVacio()]);
            setHijos([]);
            setEdad('');
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Aviso temprano de CUIL duplicado: apenas el CUIL está completo (11 dígitos) se consulta si ya
    // existe, sin esperar a "Registrar Cliente". Depende de open y del CUIL (valores primitivos), no
    // del objeto cliente, que cambia con cada tecla. Es solo un aviso: si la consulta falla no se
    // muestra nada y el backend sigue rechazando el duplicado al registrar.
    useEffect(() => {
        // El resultado anterior deja de valer apenas cambia el CUIL: no se muestra el de otro.
        setCuilExistente(null);
        const cuilLimpio = cliente.cuil.replace(/\D/g, '');
        if (!open || cuilLimpio.length !== 11) {
            return undefined;
        }
        let vigente = true;
        // Pequeña espera para no consultar dos veces si se pega o corrige el CUIL enseguida.
        const temporizador = setTimeout(async () => {
            try {
                const existencia = await consultarExistenciaCuil(cuilLimpio);
                if (vigente && existencia?.existe) {
                    setCuilExistente(existencia);
                }
            } catch (error) {
                console.error('No se pudo verificar si el CUIL ya existe', error);
            }
        }, 400);
        return () => {
            vigente = false;
            clearTimeout(temporizador);
        };
    }, [open, cliente.cuil]);

    // Calcular edad cuando cambia fechaNacimiento
    useEffect(() => {
        if (cliente.fechaNacimiento) {
            const hoy = new Date();
            const nac = new Date(cliente.fechaNacimiento);
            let age = hoy.getFullYear() - nac.getFullYear();
            const m = hoy.getMonth() - nac.getMonth();
            if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) {
                age--;
            }
            setEdad(age >= 0 ? age : '');
        } else {
            setEdad('');
        }
    }, [cliente.fechaNacimiento]);

    // Cargar niveles educativos
    useEffect(() => {
        if (open) {
            const cargarNiveles = async () => {
                try {
                    const data = await getNivelesEducativos();
                    setNiveles(data);
                } catch (error) {
                    console.error("Error al cargar niveles educativos", error);
                }
            };
            cargarNiveles();
        }
    }, [open]);

    // Cargar provincias
    useEffect(() => {
        if (open) {
            const cargarProvincias = async () => {
                try {
                    const data = await getProvincias();
                    setProvincias(data);
                } catch (error) {
                    console.error("Error al cargar provincias", error);
                }
            };
            cargarProvincias();
        }
    }, [open]);

    const actualizarDomicilio = (index, cambios) =>
        setDomicilios(prev => prev.map((d, i) => i === index ? { ...d, ...cambios } : d));

    const handleDomicilioProvinciaChange = async (index, provinciaId) => {
        actualizarDomicilio(index, {
            _provinciaId: provinciaId,
            _divisionId: '',
            _divisiones: [],
            _localidades: [],
            localidad: { id: '', localidad: '', provincia: { id: provinciaId } }
        });
        if (provinciaId) {
            try {
                const [divs, locs] = await Promise.all([
                    getDivisionesPorProvincia(provinciaId),
                    getLocalidadesPorProvincia(provinciaId)
                ]);
                actualizarDomicilio(index, { _divisiones: divs, _localidades: locs });
            } catch { actualizarDomicilio(index, { _divisiones: [], _localidades: [] }); }
        }
    };

    const handleDomicilioDivisionChange = async (index, divisionId) => {
        actualizarDomicilio(index, {
            _divisionId: divisionId,
            _localidades: [],
            localidad: { ...domicilios[index].localidad, id: '' }
        });
        if (divisionId) {
            try {
                const locs = await getLocalidadesPorDivision(divisionId);
                actualizarDomicilio(index, { _localidades: locs });
            } catch { actualizarDomicilio(index, { _localidades: [] }); }
        }
    };

    useEffect(() => {
        const cargarRefs = async () => {
            try {
                const res = await DatosMaestrosService.getReferencias();
                setReferenciasDisponibles(res.data);
            } catch (e) { console.error("Error cargando referencias", e); }
        };
        cargarRefs();
    }, []);

    const handleDomicilioLocalidadChange = (index, newValue) => {
        actualizarDomicilio(index, {
            localidad: newValue || { id: '', localidad: '', provincia: { id: domicilios[index]._provinciaId } },
            ...(newValue?.division ? { _divisionId: newValue.division.id } : {})
        });
    };

    // Manejar cambios en campos simples
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'cuil') {
            setCliente({ ...cliente, cuil: formatCuil(value) });
            return;
        }
        if (name === 'telefonoPropio') {
            const digits = value.replace(/\D/g, '').slice(0, 10);
            setCliente({ ...cliente, telefonoPropio: digits });
            return;
        }
        setCliente({ ...cliente, [name]: type === 'checkbox' ? checked : value });
    };

    const handleDomicilioCampoChange = (index, field, value) => {
        actualizarDomicilio(index, {
            [field]: (field === 'altura' || field === 'piso') ? (parseInt(value) || '') : value
        });
    };

    const agregarDomicilio = () => setDomicilios(prev => [...prev, domicilioVacio()]);
    const eliminarDomicilio = (index) => setDomicilios(prev => prev.filter((_, i) => i !== index));

    // Manejar cambios en claves
    const handleClaveChange = (field, value) => {
        setCliente({
            ...cliente,
            clave: {
                ...cliente.clave,
                [field]: value
            }
        });
    };

    // Manejar cambios en contacto (ahora maneja una lista de contactos)
    const handleContactoChange = (index, field, value) => {
        const nuevosContactos = [...contactos];
        if (field === 'nroTelefono') {
            const digits = value.replace(/\D/g, '').slice(0, 10);
            nuevosContactos[index] = { ...nuevosContactos[index], nroTelefono: digits };
        } else {
            nuevosContactos[index] = { ...nuevosContactos[index], [field]: value };
        }
        setContactos(nuevosContactos);
    };

    const agregarContacto = () => {
        setContactos([...contactos, { ...contactoInicial }]);
    };

    const eliminarContacto = (index) => {
        const nuevosContactos = contactos.filter((_, i) => i !== index);
        setContactos(nuevosContactos);
    };

    const handleHijoChange = (index, field, value) => {
        setHijos(prev => prev.map((h, i) => (i === index ? { ...h, [field]: value } : h)));
    };

    const agregarHijo = () => setHijos(prev => [...prev, hijoVacio()]);

    const eliminarHijo = (index) => setHijos(prev => prev.filter((_, i) => i !== index));

    // Calcular edad cuando cambia fecha de nacimiento
    useEffect(() => {
        if (cliente.fechaNacimiento) {
            const hoy = new Date();
            const nacimiento = new Date(cliente.fechaNacimiento);
            let edadCalculada = hoy.getFullYear() - nacimiento.getFullYear();
            const m = hoy.getMonth() - nacimiento.getMonth();
            if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
                edadCalculada--;
            }
            setEdad(edadCalculada);
        } else {
            setEdad('');
        }
    }, [cliente.fechaNacimiento]);

    // Validar formulario
    const validarCliente = () => {
        // Validar CUIL
        if (!cliente.cuil) {
            setSnackbar({ open: true, message: 'El CUIL es obligatorio', severity: 'error' });
            return false;
        }
        const cuilLimpio = cliente.cuil.replace(/\D/g, '');
        if (cuilLimpio.length !== 11) {
            setSnackbar({ open: true, message: 'El CUIL debe tener exactamente 11 dígitos numéricos', severity: 'error' });
            return false;
        }
        // Ya se avisó en pantalla al escribir el CUIL; el backend lo rechazaría igual.
        if (cuilExistente) {
            setSnackbar({ open: true, message: 'Ya existe un cliente registrado con este CUIL', severity: 'error' });
            return false;
        }

        // Validar apellido y nombre
        if (!cliente.apellido || !cliente.apellido.trim()) {
            setSnackbar({ open: true, message: 'El apellido es obligatorio', severity: 'error' });
            return false;
        }
        if (!cliente.nombre || !cliente.nombre.trim()) {
            setSnackbar({ open: true, message: 'El nombre es obligatorio', severity: 'error' });
            return false;
        }

        // Validar que apellido y nombre solo contengan letras y espacios
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(cliente.apellido)) {
            setSnackbar({ open: true, message: 'El apellido solo puede contener letras', severity: 'error' });
            return false;
        }
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(cliente.nombre)) {
            setSnackbar({ open: true, message: 'El nombre solo puede contener letras', severity: 'error' });
            return false;
        }

        // Validar longitudes máximas (la base rechaza el guardado si se excede, ver LIMITES)
        if (cliente.apellido.length > LIMITES.apellido) {
            setSnackbar({ open: true, message: `El apellido no puede superar los ${LIMITES.apellido} caracteres`, severity: 'error' });
            return false;
        }
        if (cliente.nombre.length > LIMITES.nombre) {
            setSnackbar({ open: true, message: `El nombre no puede superar los ${LIMITES.nombre} caracteres`, severity: 'error' });
            return false;
        }
        // La referencia es obligatoria en el alta (oct-2026): sin ella no se sabe cómo llegó el
        // cliente y las estadísticas de referencias lo pierden. Vale cualquiera de las tres formas
        // del selector: del catálogo, otro cliente que lo trajo o el nombre de quien lo trajo.
        const tieneReferencia = Boolean(cliente.idReferencia)
            || Boolean(cliente.referencia && cliente.referencia.trim())
            || Boolean(cliente.cuilClienteReferente)
            || Boolean(cliente.nombreReferenteExterno && cliente.nombreReferenteExterno.trim());
        if (!tieneReferencia) {
            setSnackbar({ open: true, message: 'La referencia es obligatoria: elegí una del catálogo, el cliente que lo trajo o escribí quién lo trajo.', severity: 'error' });
            return false;
        }
        if (cliente.referencia && cliente.referencia.length > LIMITES.referencia) {
            setSnackbar({ open: true, message: `La referencia no puede superar los ${LIMITES.referencia} caracteres`, severity: 'error' });
            return false;
        }
        if (cliente.nombreReferenteExterno && cliente.nombreReferenteExterno.length > LIMITES.nombreReferenteExterno) {
            setSnackbar({ open: true, message: `El nombre del referente no puede superar los ${LIMITES.nombreReferenteExterno} caracteres`, severity: 'error' });
            return false;
        }
        if (cliente.oficio && cliente.oficio.length > LIMITES.oficio) {
            setSnackbar({ open: true, message: `El oficio no puede superar los ${LIMITES.oficio} caracteres`, severity: 'error' });
            return false;
        }
        if (cliente.observacion && cliente.observacion.length > LIMITES.observacion) {
            setSnackbar({ open: true, message: `La observación no puede superar los ${LIMITES.observacion} caracteres`, severity: 'error' });
            return false;
        }
        if (cliente.conyugeNombre && cliente.conyugeNombre.length > LIMITES.conyugeNombre) {
            setSnackbar({ open: true, message: `El nombre del cónyuge no puede superar los ${LIMITES.conyugeNombre} caracteres`, severity: 'error' });
            return false;
        }
        if (cliente.nombreObraSocial && cliente.nombreObraSocial.length > LIMITES.nombreObraSocial) {
            setSnackbar({ open: true, message: `El nombre de la obra social no puede superar los ${LIMITES.nombreObraSocial} caracteres`, severity: 'error' });
            return false;
        }

        // Validar fecha de ingreso
        if (!cliente.fechaIngreso) {
            setSnackbar({ open: true, message: 'La fecha de ingreso es obligatoria', severity: 'error' });
            return false;
        }

        // Validar que la fecha no sea futura
        const fechaIngreso = new Date(cliente.fechaIngreso);
        const hoy = new Date();
        if (fechaIngreso > hoy) {
            setSnackbar({ open: true, message: 'La fecha de ingreso no puede ser futura', severity: 'error' });
            return false;
        }

        // Validar fecha nacimiento
        if (cliente.fechaNacimiento) {
             const fechaNac = new Date(cliente.fechaNacimiento);
             if (fechaNac > hoy) {
                 setSnackbar({ open: true, message: 'La fecha de nacimiento no puede ser futura', severity: 'error' });
                 return false;
             }
        }

        // Validar email si está presente
        if (cliente.mail && cliente.mail.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(cliente.mail)) {
                setSnackbar({ open: true, message: 'El email no tiene un formato válido', severity: 'error' });
                return false;
            }
            if (cliente.mail.length > LIMITES.mail) {
                setSnackbar({ open: true, message: `El email no puede superar los ${LIMITES.mail} caracteres`, severity: 'error' });
                return false;
            }
        }

        // Validar teléfono si está presente
        if (cliente.telefonoPropio && !isTelefonoCordobaValido(cliente.telefonoPropio)) {
            setSnackbar({ open: true, message: 'El teléfono debe tener 10 dígitos', severity: 'error' });
            return false;
        }

        for (let i = 0; i < contactos.length; i++) {
            const contacto = contactos[i];
            const tieneDatos = contacto.nroTelefono?.trim() || contacto.relacion?.trim() || contacto.nombre?.trim();
            if (!tieneDatos) {
                continue;
            }

            if (!isTelefonoCordobaValido(contacto.nroTelefono)) {
                setSnackbar({
                    open: true,
                    message: `El contacto ${i + 1} debe tener 10 dígitos`,
                    severity: 'error'
                });
                return false;
            }

            if (contacto.nombre && contacto.nombre.length > LIMITES.contactoNombre) {
                setSnackbar({
                    open: true,
                    message: `El nombre del contacto ${i + 1} no puede superar los ${LIMITES.contactoNombre} caracteres`,
                    severity: 'error'
                });
                return false;
            }
        }

        // Validar domicilios: si se ingresó algo, debe estar completo
        for (let i = 0; i < domicilios.length; i++) {
            const dom = domicilios[i];
            const tieneCalle = dom.calle?.trim();
            const tieneLocalidad = dom.localidad?.id;
            const tieneAlgo = tieneCalle || dom.altura || tieneLocalidad || dom.piso || dom.departamento || dom.barrio?.trim();
            if (tieneAlgo) {
                if (!tieneCalle) {
                    setSnackbar({ open: true, message: `El domicilio ${i + 1} requiere la calle`, severity: 'error' });
                    return false;
                }
                if (!tieneLocalidad) {
                    setSnackbar({ open: true, message: `El domicilio ${i + 1} requiere localidad`, severity: 'error' });
                    return false;
                }
            }
            if (dom.calle && dom.calle.length > LIMITES.domicilioCalle) {
                setSnackbar({ open: true, message: `La calle del domicilio ${i + 1} no puede superar los ${LIMITES.domicilioCalle} caracteres`, severity: 'error' });
                return false;
            }
            if (dom.barrio && dom.barrio.length > LIMITES.domicilioBarrio) {
                setSnackbar({ open: true, message: `El barrio del domicilio ${i + 1} no puede superar los ${LIMITES.domicilioBarrio} caracteres`, severity: 'error' });
                return false;
            }
            if (dom.departamento && String(dom.departamento).length > LIMITES.domicilioDepartamento) {
                setSnackbar({ open: true, message: `El depto del domicilio ${i + 1} no puede superar los ${LIMITES.domicilioDepartamento} caracteres`, severity: 'error' });
                return false;
            }
        }

        // Validar longitud del nombre de los hijos cargados
        for (let i = 0; i < hijos.length; i++) {
            if (hijos[i].nombre && hijos[i].nombre.length > LIMITES.hijoNombre) {
                setSnackbar({ open: true, message: `El nombre del hijo ${i + 1} no puede superar los ${LIMITES.hijoNombre} caracteres`, severity: 'error' });
                return false;
            }
        }

        return true;
    };

    // Limpiar objeto antes de enviar (remover campos vacíos)
    const limpiarClienteDto = (clienteDto) => {
        const dto = { ...clienteDto };

        // Asegurar que el CUIL vaya al backend sin guiones
        if (dto.cuil) {
            dto.cuil = dto.cuil.replace(/\D/g, '');
        }

        // Si no hay nivel educativo, no enviarlo
        if (!dto.nivelEducativo?.id) {
            delete dto.nivelEducativo;
        }

        delete dto.domicilio;
        delete dto.domicilios;

        // Si no hay claves, no enviarlas
        if (!dto.clave?.claveAfip && !dto.clave?.claveSs && !dto.clave?.claveCidi) {
            delete dto.clave;
        }

        // Convertir campos vacíos a null
        if (!dto.telefonoPropio) dto.telefonoPropio = null;
        if (!dto.mail) dto.mail = null;
        if (!dto.referencia) dto.referencia = null;
        if (!dto.cuilClienteReferente) dto.cuilClienteReferente = null;
        if (!dto.nombreReferenteExterno) dto.nombreReferenteExterno = null;
        // Derivados de solo lectura: los recalcula el backend al mapear la respuesta.
        delete dto.nombreClienteReferente;
        delete dto.referenciaHeredada;
        if (!dto.observacion) dto.observacion = null;
        if (!dto.oficio) dto.oficio = null;

        return dto;
    };

    // Enviar formulario
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validarCliente()) return;

        try {
            const clienteDto = limpiarClienteDto({ ...cliente });
            clienteDto.contactos = contactos
                .filter(c => c.nroTelefono || c.nombre || c.relacion)
                .map(c => ({
                    ...c,
                    nroTelefono: normalizeTelefonoForStorage(c.nroTelefono)
                }));
            const domiciliosValidos = domicilios
                .filter(d => d.calle?.trim() && d.localidad?.id)
                .map(limpiarDomicilioParaGuardar);
            clienteDto.domicilios = domiciliosValidos;
            clienteDto.domicilio = domiciliosValidos[0] || null;

            // Se envían todas las filas, aunque estén vacías: "Agregar hijo" sin llenar nada es
            // el flujo real del estudio para dejar constancia de la cantidad de hijos cuando
            // todavía no tienen sus datos (nombre, fecha de nacimiento, AUH/CUD). El backend
            // además ignora esto si el género no es FEMENINO.
            clienteDto.hijos = hijos;

            const clienteGuardado = await crearCliente(clienteDto);
            setClienteCreado(clienteGuardado);
            setSnackbar({ open: true, message: 'Cliente registrado con éxito', severity: 'success' });
            onSuccess && onSuccess(clienteGuardado);
            setCliente({ ...clienteInicial, fechaIngreso: getFechaHoy(), fechaNacimiento: '' });
            setContactos([{ ...contactoInicial }]);
            setDomicilios([domicilioVacio()]);
            setHijos([]);
            setEdad('');
            setDialogoPaso(1);
            onClose && onClose();
            if (mostrarDialogoConfirmacion) {
                setDialogoConfirmar(true);
            }
        } catch (err) {
            console.error('Error al registrar cliente:', err);
            // El backend ahora siempre devuelve { message } en errores de este endpoint
            // (ClienteExcepcion, validación de longitud, o cualquier otro error inesperado vía
            // GlobalExceptionHandler) — usarlo primero evita mostrar "CUIL duplicado" o "error
            // interno" ante un error que en realidad es otro (ver incidente del 09/09/2026).
            let errorMessage = err.response?.data?.message;
            if (!errorMessage) {
                if (typeof err.response?.data === 'string') {
                    errorMessage = err.response.data;
                } else if (err.response?.status === 400) {
                    errorMessage = 'El cliente ya está registrado con este CUIL. Intente con otro CUIL diferente.';
                } else if (err.response?.status === 500) {
                    errorMessage = 'Error interno del servidor. Por favor, intente más tarde.';
                } else {
                    errorMessage = 'Error al registrar el cliente';
                }
            }
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        }
    };

    // Cerrar modal y resetear
    const handleClose = () => {
        setCliente({ ...clienteInicial, fechaIngreso: getFechaHoy() });
        setContactos([{ ...contactoInicial }]);
        setDomicilios([domicilioVacio()]);
        setHijos([]);
        setEdad('');
        onClose && onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        boxShadow: "var(--shadow-lg)",
                        maxHeight: "90vh"
                    }
                }}
            >
                {/* Header Moderno con Gradiente Azul */}
                <div style={{
                    background: "var(--primary)",
                    padding: "1.5rem 2rem",
                    color: "white",
                    position: "relative"
                }}>
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                            <i className="bi bi-person-plus-fill" style={{fontSize: "2rem"}}></i>
                            <h5 className="mb-0 fw-bold">Registrar Cliente</h5>
                        </div>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            sx={{
                                color: "white",
                                background: "rgba(255,255,255,0.2)",
                                borderRadius: "8px",
                                padding: "0.5rem",
                                "&:hover": {
                                    background: "rgba(255,255,255,0.3)"
                                }
                            }}
                        >
            <CloseIcon />
        </IconButton>
    </div>
</div>                <form onSubmit={handleSubmit}>
                    <DialogContent sx={{padding: "2rem"}}>
                        <Stack spacing={3}>
                            <>
                            {/* ===== DATOS PERSONALES ===== */}
                            <Accordion defaultExpanded sx={{
                                borderRadius: "12px !important",
                                border: "2px solid #e5e7eb",
                                boxShadow: "var(--shadow-sm)",
                                "&:before": { display: "none" }
                            }}>
                                <AccordionSummary 
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        background: "var(--gradient-primary-light)",
                                        borderRadius: "12px"
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)' }}>
                                        <i className="bi bi-person-lines-fill me-2"></i>Datos Personales
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={4}>
                                                <TextField
                                                    label="CUIL"
                                                    name="cuil"
                                                    value={cliente.cuil}
                                                    onChange={handleChange}
                                                    required
                                                    fullWidth
                                                    error={Boolean(cuilExistente)}
                                                    inputProps={{ maxLength: 13 }}
                                                />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <TextField
                                                    label="Apellido"
                                                    name="apellido"
                                                    value={cliente.apellido}
                                                    onChange={handleChange}
                                                    required
                                                    fullWidth
                                                    inputProps={{ maxLength: LIMITES.apellido }}
                                                />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <TextField
                                                    label="Nombre"
                                                    name="nombre"
                                                    value={cliente.nombre}
                                                    onChange={handleChange}
                                                    required
                                                    fullWidth
                                                    inputProps={{ maxLength: LIMITES.nombre }}
                                                />
                                            </Grid>
                                        </Grid>

                                        {cuilExistente && (
                                            <Alert severity="warning">
                                                Ya existe un cliente con este CUIL:{' '}
                                                <strong>{apellidoNombre(cuilExistente.apellido, cuilExistente.nombre)}</strong>
                                                {cuilExistente.activo
                                                    ? '. No hace falta registrarlo de nuevo.'
                                                    : ', pero está dado de baja. Reactivalo desde su ficha en vez de registrarlo de nuevo.'}
                                            </Alert>
                                        )}

                                        {/* Si no es cliente pero figura en CARPETAS ELIMINADAS o ARCHIVO de la
                                            planilla, ya pasó por el estudio: se avisa con qué trámite y cuándo. */}
                                        {!cuilExistente && <AvisoYaFueCliente cuil={cliente.cuil} />}

                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="Fecha de Ingreso"
                                                    name="fechaIngreso"
                                                    type="date"
                                                    value={cliente.fechaIngreso}
                                                    onChange={handleChange}
                                                    required
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                        </Grid>

                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="Fecha de Nacimiento"
                                                    name="fechaNacimiento"
                                                    type="date"
                                                    value={cliente.fechaNacimiento || ''}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="Edad"
                                                    value={edad}
                                                    fullWidth
                                                    disabled
                                                    InputProps={{
                                                        readOnly: true,
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>

                                        <TextField
                                            label="Teléfono"
                                            name="telefonoPropio"
                                            value={cliente.telefonoPropio || ''}
                                            onChange={handleChange}
                                            fullWidth
                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
                                            helperText="Ingresar 10 dígitos. Ejemplo: 3517151340"
                                        />

                                        <TextField
                                            label="Email"
                                            name="mail"
                                            type="email"
                                            value={cliente.mail}
                                            onChange={handleChange}
                                            fullWidth
                                            inputProps={{ maxLength: LIMITES.mail }}
                                        />

                                        <TextField
                                            select
                                            label="Nivel Educativo"
                                            value={cliente.nivelEducativo?.id || ''}
                                            onChange={(e) => setCliente({
                                                ...cliente,
                                                nivelEducativo: { id: Number(e.target.value) }
                                            })}
                                            fullWidth
                                        >
                                            <MenuItem value="">Seleccionar</MenuItem>
                                            {niveles.map(n => (
                                                <MenuItem key={n.id} value={n.id}>{n.nombre}</MenuItem>
                                            ))}
                                        </TextField>

                                        <TextField
                                            label="Observación"
                                            name="observacion"
                                            value={cliente.observacion}
                                            onChange={handleChange}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            inputProps={{ maxLength: LIMITES.observacion }}
                                        />

                                        <TextField
                                            label="Oficio / Actividad"
                                            name="oficio"
                                            value={cliente.oficio}
                                            onChange={handleChange}
                                            fullWidth
                                            inputProps={{ maxLength: LIMITES.oficio }}
                                        />

                                        <Grid container spacing={2}>
                                            <Grid item>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={cliente.dosDoce}
                                                            onChange={(e) => setCliente({ ...cliente, dosDoce: e.target.checked })}
                                                        />
                                                    }
                                                    label="212"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>

                            {/* ===== GÉNERO, HIJOS Y OBRA SOCIAL ===== */}
                            <Accordion sx={{
                                borderRadius: "12px !important",
                                border: "2px solid #e5e7eb",
                                boxShadow: "var(--shadow-sm)",
                                "&:before": { display: "none" }
                            }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        background: "var(--gradient-primary-light)",
                                        borderRadius: "12px"
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)' }}>
                                        <i className="bi bi-person-hearts me-2"></i>Género, Hijos y Obra Social
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        <TextField
                                            select
                                            label="Género"
                                            name="genero"
                                            value={cliente.genero || ''}
                                            onChange={handleChange}
                                            fullWidth
                                        >
                                            <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                            <MenuItem value="FEMENINO">Femenino</MenuItem>
                                            <MenuItem value="MASCULINO">Masculino</MenuItem>
                                            <MenuItem value="OTRO">Otro</MenuItem>
                                        </TextField>

                                        <TextField
                                            select
                                            label="Estado civil"
                                            name="estadoCivil"
                                            value={cliente.estadoCivil || ''}
                                            onChange={handleChange}
                                            fullWidth
                                        >
                                            <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                            <MenuItem value="SOLTERO">Soltero/a</MenuItem>
                                            <MenuItem value="CASADO">Casado/a</MenuItem>
                                            <MenuItem value="DIVORCIADO">Divorciado/a</MenuItem>
                                            <MenuItem value="VIUDO">Viudo/a</MenuItem>
                                            <MenuItem value="SEPARADO">Separado/a</MenuItem>
                                            <MenuItem value="CONCUBINATO">Concubinato</MenuItem>
                                        </TextField>
                                        {(cliente.estadoCivil === 'CASADO' || cliente.estadoCivil === 'CONCUBINATO') && (
                                            <TextField
                                                label="Nombre del cónyuge"
                                                name="conyugeNombre"
                                                value={cliente.conyugeNombre || ''}
                                                onChange={handleChange}
                                                fullWidth
                                                inputProps={{ maxLength: LIMITES.conyugeNombre }}
                                            />
                                        )}

                                        {cliente.genero === 'FEMENINO' && (
                                            <div>
                                                <Typography variant="subtitle2" fontWeight={600} color="#1e40af" sx={{ mb: 1 }}>
                                                    <i className="bi bi-people-fill me-2"></i>Hijos
                                                </Typography>
                                                <Stack spacing={2}>
                                                    {hijos.map((hijo, index) => (
                                                        <div key={index} style={{
                                                            padding: '16px',
                                                            border: "1px solid #e0e0e0",
                                                            borderRadius: "8px",
                                                            position: 'relative',
                                                            backgroundColor: '#fafafa'
                                                        }}>
                                                            <Grid container spacing={2}>
                                                                <Grid item xs={12} sm={6}>
                                                                    <TextField
                                                                        label="Nombre"
                                                                        value={hijo.nombre || ''}
                                                                        onChange={(e) => handleHijoChange(index, 'nombre', e.target.value)}
                                                                        fullWidth
                                                                        inputProps={{ maxLength: LIMITES.hijoNombre }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <TextField
                                                                        label="Fecha de nacimiento"
                                                                        type="date"
                                                                        value={hijo.fechaNacimiento || ''}
                                                                        onChange={(e) => handleHijoChange(index, 'fechaNacimiento', e.target.value)}
                                                                        fullWidth
                                                                        InputLabelProps={{ shrink: true }}
                                                                        helperText={hijo.fechaNacimiento ? `Edad: ${calcularEdad(hijo.fechaNacimiento)}` : ' '}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                            <Stack direction="row" flexWrap="wrap" sx={{ mt: 1 }}>
                                                                <FormControlLabel control={<Checkbox checked={!!hijo.tieneAuh} onChange={(e) => handleHijoChange(index, 'tieneAuh', e.target.checked)} />} label="AUH" />
                                                                <FormControlLabel control={<Checkbox checked={!!hijo.tieneCud} onChange={(e) => handleHijoChange(index, 'tieneCud', e.target.checked)} />} label="CUD" />
                                                                <FormControlLabel control={<Checkbox checked={!!hijo.esAdoptado} onChange={(e) => handleHijoChange(index, 'esAdoptado', e.target.checked)} />} label="Adoptado/a" />
                                                            </Stack>
                                                            <IconButton
                                                                onClick={() => eliminarHijo(index)}
                                                                sx={{ position: 'absolute', top: 5, right: 5, color: 'error.main' }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </div>
                                                    ))}
                                                    <Button
                                                        startIcon={<AddIcon />}
                                                        onClick={agregarHijo}
                                                        variant="outlined"
                                                        sx={{ alignSelf: 'flex-start' }}
                                                    >
                                                        Agregar hijo
                                                    </Button>
                                                </Stack>
                                            </div>
                                        )}

                                        <Typography variant="subtitle2" fontWeight={600} color="#1e40af">
                                            <i className="bi bi-hospital me-2"></i>Obra Social
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!cliente.tieneObraSocial}
                                                    onChange={(e) => setCliente({ ...cliente, tieneObraSocial: e.target.checked })}
                                                />
                                            }
                                            label="Tiene obra social"
                                        />
                                        {cliente.tieneObraSocial && (
                                            <>
                                                <TextField
                                                    label="Nombre de la obra social"
                                                    name="nombreObraSocial"
                                                    value={cliente.nombreObraSocial || ''}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    inputProps={{ maxLength: LIMITES.nombreObraSocial }}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={!!cliente.mantieneObraSocialPostJubilacion}
                                                            onChange={(e) => setCliente({ ...cliente, mantieneObraSocialPostJubilacion: e.target.checked })}
                                                        />
                                                    }
                                                    label="¿Mantiene la obra social después de jubilarse?"
                                                />
                                            </>
                                        )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion sx={{
                                borderRadius: "12px !important",
                                border: "2px solid #e5e7eb",
                                boxShadow: "var(--shadow-sm)",
                                "&:before": { display: "none" }
                            }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        background: "var(--gradient-primary-light)",
                                        borderRadius: "12px"
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)' }}>
                                        <i className="bi bi-house-door-fill me-2"></i>Domicilios
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        {domicilios.map((dom, idx) => (
                                            <div key={idx} style={{
                                                padding: '16px',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '8px',
                                                position: 'relative'
                                            }}>
                                                {domicilios.length > 1 && (
                                                    <IconButton
                                                        onClick={() => eliminarDomicilio(idx)}
                                                        size="small"
                                                        sx={{ position: 'absolute', top: 6, right: 6, color: 'error.main' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, mb: 1, display: 'block' }}>
                                                    Domicilio {idx + 1}
                                                </Typography>
                                                <Stack spacing={2}>
                                                    <TextField
                                                        select
                                                        label="Tipo de domicilio"
                                                        value={dom.tipo || ''}
                                                        onChange={(e) => handleDomicilioCampoChange(idx, 'tipo', e.target.value)}
                                                        fullWidth
                                                        variant="outlined"
                                                        helperText="Usado por la Carátula (DOMICILIO DNI / DOMICILIO REAL)"
                                                    >
                                                        <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                                        <MenuItem value="DNI">Domicilio del DNI</MenuItem>
                                                        <MenuItem value="REAL">Domicilio real</MenuItem>
                                                        <MenuItem value="OTRO">Otro</MenuItem>
                                                    </TextField>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                select
                                                                label="Provincia"
                                                                value={dom._provinciaId || ''}
                                                                onChange={(e) => handleDomicilioProvinciaChange(idx, Number(e.target.value))}
                                                                fullWidth
                                                                sx={{ minWidth: '160px' }}
                                                                variant="outlined"
                                                            >
                                                                <MenuItem value="">Seleccionar</MenuItem>
                                                                {provincias.map(p => (
                                                                    <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                select
                                                                label="Departamento/Partido"
                                                                value={dom._divisionId || ''}
                                                                onChange={(e) => handleDomicilioDivisionChange(idx, Number(e.target.value))}
                                                                fullWidth
                                                                sx={{ minWidth: '160px' }}
                                                                variant="outlined"
                                                                disabled={!dom._divisiones?.length}
                                                            >
                                                                <MenuItem value="">Seleccionar</MenuItem>
                                                                {(dom._divisiones || []).map(d => (
                                                                    <MenuItem key={d.id} value={d.id}>{d.nombre}</MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <Autocomplete
                                                                disablePortal
                                                                options={dom._localidades || []}
                                                                getOptionLabel={(option) => (option.localidad || option.nombre || "")}
                                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                value={(dom._localidades || []).find(l => l.id === dom.localidad?.id) || null}
                                                                onChange={(e, v) => handleDomicilioLocalidadChange(idx, v)}
                                                                disabled={!dom._localidades?.length}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} label="Localidad" fullWidth sx={{ minWidth: '160px' }} variant="outlined" />
                                                                )}
                                                                noOptionsText="Sin resultados"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                label="Barrio"
                                                                value={dom.barrio || ''}
                                                                onChange={(e) => handleDomicilioCampoChange(idx, 'barrio', e.target.value)}
                                                                fullWidth
                                                                variant="outlined"
                                                                inputProps={{ maxLength: LIMITES.domicilioBarrio }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                label="Calle"
                                                                value={dom.calle || ''}
                                                                onChange={(e) => handleDomicilioCampoChange(idx, 'calle', e.target.value)}
                                                                fullWidth
                                                                variant="outlined"
                                                                inputProps={{ maxLength: LIMITES.domicilioCalle }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                label="Altura"
                                                                type="number"
                                                                value={dom.altura || ''}
                                                                onChange={(e) => handleDomicilioCampoChange(idx, 'altura', e.target.value)}
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                label="Piso"
                                                                type="number"
                                                                value={dom.piso || ''}
                                                                onChange={(e) => handleDomicilioCampoChange(idx, 'piso', e.target.value)}
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                label="Depto"
                                                                value={dom.departamento || ''}
                                                                onChange={(e) => handleDomicilioCampoChange(idx, 'departamento', e.target.value)}
                                                                fullWidth
                                                                variant="outlined"
                                                                inputProps={{ maxLength: LIMITES.domicilioDepartamento }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                            </div>
                                        ))}
                                        {domicilios.length < 2 && (
                                            <Button
                                                startIcon={<AddIcon />}
                                                onClick={agregarDomicilio}
                                                variant="outlined"
                                                sx={{ mt: 1, alignSelf: 'flex-start' }}
                                            >
                                                Agregar domicilio DNI
                                            </Button>
                                        )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>

                            {/* ===== CONTACTO ADICIONAL ===== */}
                            <Accordion sx={{
                                borderRadius: "12px !important",
                                border: "2px solid #e5e7eb",
                                boxShadow: "var(--shadow-sm)",
                                "&:before": { display: "none" }
                            }}>
                                <AccordionSummary 
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        background: "var(--gradient-primary-light)",
                                        borderRadius: "12px"
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)' }}>
                                        <i className="bi bi-telephone me-2"></i>Contactos Adicionales
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        {contactos.map((contactoItem, index) => (
                                            <div key={index} style={{
                                                padding: '16px',
                                                border: "1px solid #e0e0e0",
                                                borderRadius: "8px",
                                                position: 'relative',
                                                backgroundColor: '#fafafa'
                                            }}>
                                                <Stack spacing={2}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label="Nombre (Opcional)"
                                                                value={contactoItem.nombre || ''}
                                                                onChange={(e) => handleContactoChange(index, 'nombre', e.target.value)}
                                                                fullWidth
                                                                helperText="Nombre del contacto"
                                                                inputProps={{ maxLength: LIMITES.contactoNombre }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                label="Teléfono"
                                                                value={contactoItem.nroTelefono || ''}
                                                                onChange={(e) => handleContactoChange(index, 'nroTelefono', e.target.value)}
                                                                fullWidth
                                                                helperText="Ingresar 10 dígitos. Ejemplo: 3517151340"
                                                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                select
                                                                label="Relación"
                                                                value={contactoItem.relacion || ''}
                                                                onChange={(e) => handleContactoChange(index, 'relacion', e.target.value)}
                                                                fullWidth
                                                                helperText="Relación con el cliente"
                                                            >
                                                                <MenuItem value="">Seleccionar</MenuItem>
                                                                <MenuItem value="Hijo/a">Hijo/a</MenuItem>
                                                                <MenuItem value="Esposo/a">Esposo/a</MenuItem>
                                                                <MenuItem value="Nieto/a">Nieto/a</MenuItem>
                                                                <MenuItem value="Hermano/a">Hermano/a</MenuItem>
                                                                <MenuItem value="Padre/Madre">Padre/Madre</MenuItem>
                                                                <MenuItem value="Amigo/a">Amigo/a</MenuItem>
                                                                <MenuItem value="Otro">Otro</MenuItem>
                                                            </TextField>
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                                {contactos.length > 1 && (
                                                    <IconButton
                                                        onClick={() => eliminarContacto(index)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 5,
                                                            right: 5,
                                                            color: 'error.main'
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </div>
                                        ))}

                                        <Button
                                            startIcon={<AddIcon />}
                                            onClick={agregarContacto}
                                            variant="outlined"
                                            sx={{ mt: 1, alignSelf: 'flex-start' }}
                                        >
                                            Agregar otro contacto
                                        </Button>
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>

                            {/* ===== CLAVES ===== */}
                            <Accordion sx={{
                                borderRadius: "12px !important",
                                border: "2px solid #e5e7eb",
                                boxShadow: "var(--shadow-sm)",
                                "&:before": { display: "none" }
                            }}>
                                <AccordionSummary 
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        background: "var(--gradient-primary-light)",
                                        borderRadius: "12px"
                                    }}
                            >
                                <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)' }}>
                                    <i className="bi bi-key me-2"></i>Claves
                                </Typography>
                            </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        <TextField
                                            label="Clave AFIP"
                                            value={cliente.clave?.claveAfip || ''}
                                            onChange={(e) => handleClaveChange('claveAfip', e.target.value)}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Clave Seguridad Social"
                                            value={cliente.clave?.claveSs || ''}
                                            onChange={(e) => handleClaveChange('claveSs', e.target.value)}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Clave CiDi"
                                            value={cliente.clave?.claveCidi || ''}
                                            onChange={(e) => handleClaveChange('claveCidi', e.target.value)}
                                            fullWidth
                                        />
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>

                            {/* ===== REFERENCIA (obligatoria: abierta de entrada) ===== */}
                            <Accordion defaultExpanded sx={{
                                borderRadius: "12px !important",
                                border: "2px solid #e5e7eb",
                                boxShadow: "var(--shadow-sm)",
                                "&:before": { display: "none" }
                            }}>
                                <AccordionSummary 
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        background: "var(--gradient-primary-light)",
                                        borderRadius: "12px"
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)' }}>
                                        <i className="bi bi-info-circle me-2"></i>Referencia *
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid item xs={12} md={6}>
                                        <SelectorReferenciaCliente
                                            value={cliente}
                                            onChange={(cambios) => setCliente(prev => ({ ...prev, ...cambios }))}
                                            referenciasDisponibles={referenciasDisponibles}
                                            onCrearNuevaReferencia={(nuevaRef) => {
                                                setReferenciasDisponibles(prev => [...prev, nuevaRef]);
                                                setCliente(prev => ({ ...prev, referencia: nuevaRef.nombre, idReferencia: nuevaRef.id }));
                                            }}
                                            icon={BookmarkIcon}
                                        />
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                            </>
                        </Stack>
                    </DialogContent>

                    <DialogActions sx={{
                        padding: "1.5rem 2rem",
                        borderTop: "2px solid #f3f4f6",
                        background: "#fafafa"
                    }}>
                        <Button 
                            onClick={handleClose}
                            sx={{
                                background: "white",
                                color: "#6b7280",
                                border: "2px solid #e5e7eb",
                                borderRadius: "10px",
                                padding: "0.625rem 1.5rem",
                                fontWeight: 500,
                                textTransform: "none",
                                "&:hover": {
                                    background: "#f9fafb",
                                    border: "2px solid #d1d5db"
                                }
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            sx={{
                                background: 'var(--primary)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '0.625rem 1.5rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: 'var(--shadow-md)',
                                '&:hover': {
                                    background: 'var(--primary)',
                                    boxShadow: 'var(--shadow-md)'
                                }
                            }}
                        >
                            <i className="bi bi-check-circle me-2"></i>
                            Registrar Cliente
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Snackbar para mensajes */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Diálogo post-registro */}
            <Dialog
                open={dialogoConfirmar}
                onClose={() => { setDialogoConfirmar(false); setDialogoPaso(1); }}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', boxShadow: 'var(--shadow-lg)' } }}
            >
                {dialogoPaso === 1 && (
                    <>
                        <DialogTitle sx={{ fontWeight: 'bold', color: '#166534', display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                            <i className="bi bi-check-circle-fill" style={{ color: '#22c55e', fontSize: '1.3rem' }}></i>
                            Cliente registrado exitosamente
                        </DialogTitle>
                        <DialogContent dividers sx={{ pt: 2 }}>
                            {clienteCreado && (
                                <Typography sx={{ mb: 2, color: '#374151', fontWeight: 500 }}>
                                    {apellidoNombre(clienteCreado.apellido, clienteCreado.nombre)} — CUIL: {clienteCreado.cuil}
                                </Typography>
                            )}
                            <Typography variant="body2" color="#64748b" sx={{ mb: 2 }}>
                                ¿Qué desea hacer a continuación?
                            </Typography>
                            <Stack spacing={1.5}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        // Abre en pestaña nueva (no navigate): este modal se usa desde
                                        // páginas con estado en progreso (atención, alta de trámite) y
                                        // navegar el SPA las destruye sin forma de recuperarlas al volver
                                        // atrás. Mismo criterio que VistaDetalladaAtencion.abrirHistorialLaboral.
                                        const cuil = String(clienteCreado?.cuil || '').replace(/\D/g, '');
                                        const url = `/herramientas/historial-laboral${cuil ? `?cuil=${cuil}` : ''}`;
                                        window.open(url, '_blank', 'noopener');
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#bfdbfe', color: '#1e40af', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-clock-history me-2"></i>
                                    Cargar historial laboral
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => setDialogoPaso(2)}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#bbf7d0', color: '#15803d', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <BookmarkIcon sx={{ mr: 1, fontSize: '1rem' }} />
                                    Iniciar un trámite
                                </Button>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <Button
                                onClick={() => { setDialogoConfirmar(false); setDialogoPaso(1); }}
                                sx={{ color: '#6b7280', textTransform: 'none' }}
                            >
                                Solo registrar, cerrar
                            </Button>
                        </DialogActions>
                    </>
                )}

                {dialogoPaso === 2 && (
                    <>
                        <DialogTitle sx={{ fontWeight: 'bold', color: '#1e40af', display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                            <i className="bi bi-diagram-3-fill me-2"></i>
                            Seleccionar tipo de trámite
                        </DialogTitle>
                        <DialogContent dividers sx={{ pt: 2 }}>
                            <Typography variant="body2" color="#64748b" sx={{ mb: 2 }}>
                                ¿Qué tipo de trámite desea iniciar para este cliente?
                            </Typography>
                            <Stack spacing={2}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/art/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#fbcfe8', color: '#be185d', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-heart-pulse me-2"></i>
                                    Trámite ART
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/rti/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#bbf7d0', color: '#15803d', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-file-earmark-medical me-2"></i>
                                    Trámite RTI
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/jubilacion/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#bfdbfe', color: '#1e40af', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-person-vcard me-2"></i>
                                    Trámite Jubilación
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/puam/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#ddd6fe', color: '#7c3aed', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-person-badge me-2"></i>
                                    Trámite PUAM
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/plan-pago/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#a5f3fc', color: '#0891b2', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-cash-coin me-2"></i>
                                    Trámite Plan de Pago 27.705
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/reajuste/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#fed7aa', color: '#c2410c', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-graph-up-arrow me-2"></i>
                                    Trámite Reajuste de Haberes
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/reconocimiento/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#fef08a', color: '#a16207', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-award-fill me-2"></i>
                                    Trámite Reconocimiento de Servicios
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/pnc/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#c7d2fe', color: '#4338ca', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-file-earmark-medical me-2"></i>
                                    Trámite PNC
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/amparo-pnc/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#fecaca', color: '#b91c1c', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-shield-exclamation me-2"></i>
                                    Trámite Amparo PNC
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/amparo-ley-16986/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#fecaca', color: '#b91c1c', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-shield-exclamation me-2"></i>
                                    Trámite Amparo Ley 16.986
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/amparo/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#fecdd3', color: '#be123c', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-shield-exclamation me-2"></i>
                                    Trámite Amparo por Mora (ANSES)
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/pension/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#f5d0fe', color: '#a21caf', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-people me-2"></i>
                                    Trámite Pensión
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/ganancias/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#a7f3d0', color: '#047857', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-percent me-2"></i>
                                    Trámite Impuesto a las Ganancias
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/minusvalia/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#c7d2fe', color: '#4338ca', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-person-wheelchair me-2"></i>
                                    Trámite Minusvalía
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setDialogoConfirmar(false);
                                        setDialogoPaso(1);
                                        navigate(`/declaratoria/tramites/iniciar-tramite/${clienteCreado?.cuil}`);
                                    }}
                                    sx={{ justifyContent: 'flex-start', borderRadius: '10px', py: 1.5, borderColor: '#fecdd3', color: '#9f1239', textTransform: 'none', fontWeight: 600 }}
                                >
                                    <i className="bi bi-diagram-3 me-2"></i>
                                    Trámite Declaratoria de Herederos
                                </Button>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={() => setDialogoPaso(1)} sx={{ color: '#6b7280', textTransform: 'none' }}>
                                <i className="bi bi-arrow-left me-1"></i> Volver
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

        </>
    );
}
